import {
	useEditorProps,
	MergeTagBadge,
	getEditorRoot,
	getShadowRoot,
	FIXED_CONTAINER_ID,
	RICH_TEXT_BAR_ID,
	DATA_CONTENT_EDITABLE_IDX,
	DATA_CONTENT_EDITABLE_TYPE,
	ContentEditableType,
} from '@bay-email/editor';
import React, { useCallback, useEffect } from 'react';
import type { Editor } from '@tiptap/core';
import { InlineText, InlineTextProps } from '../InlineTextField';
import { FloatingToolbar } from '../RichTextToolBar/FloatingToolbar';
import { useActiveEditable } from '../TiptapEditable/ActiveEditableProvider';
import { Field, FieldInputProps } from 'react-final-form';
import { debounce } from 'lodash';

export const RichTextField = (props: Omit<InlineTextProps, 'onChange' | 'mutators'>) => {
	const { active, setActive } = useActiveEditable();
	const contentEditableName = active?.idx ?? '';
	const contentEditableType = active?.type ?? null;

	useEffect(() => {
		const onClick = (e: MouseEvent) => {
			if (getEditorRoot()?.contains(e.target as Node)) {
				return;
			}
			const fixedContainer = document.getElementById(FIXED_CONTAINER_ID);
			const toolbarPlaceholder = document.getElementById('rich-text-toolbar-placeholder');
			const floatingToolbar = document.getElementById(RICH_TEXT_BAR_ID);
			const target = e.target as HTMLElement;

			// Don't hide toolbar if clicking on toolbar or its dropdowns/overlays/color pickers
			if (
				fixedContainer?.contains(e.target as Node) ||
				toolbarPlaceholder?.contains(e.target as Node) ||
				floatingToolbar?.contains(e.target as Node) ||
				target.closest('.arco-dropdown') ||
				target.closest('.arco-trigger-popup') ||
				target.closest('.arco-trigger') ||
				target.closest('.bay-email-color-picker-popup') ||
				target.closest('.bay-email-native-color-picker-popup') ||
				target.closest('.bay-color-picker__popover') ||
				target.closest('.bay-color-picker')
			) {
				return;
			}
			setActive(null);
		};

		window.addEventListener('click', onClick);
		return () => {
			window.removeEventListener('click', onClick);
		};
	}, [setActive]);

	useEffect(() => {
		const root = getShadowRoot();
		if (!root) return;

		const applyEditableState = (editableElement: Element | null) => {
			if (!editableElement) return false;

			const idxName = editableElement.getAttribute(DATA_CONTENT_EDITABLE_IDX);
			if (!idxName) return false;

			const type = editableElement.getAttribute(DATA_CONTENT_EDITABLE_TYPE);
			setActive({
				idx: idxName,
				type:
					type === ContentEditableType.RichText
						? ContentEditableType.RichText
						: ContentEditableType.Text,
			});
			return true;
		};

		const getActiveEditableElement = () => {
			const activeElement = getShadowRoot().activeElement;
			if (
				activeElement instanceof Element &&
				activeElement.getAttribute('contenteditable')
			) {
				return activeElement;
			}

			return null;
		};

		const onClick = (e: Event) => {
			const target = e.target instanceof Element ? e.target : null;

			const fixedContainer = document.getElementById(FIXED_CONTAINER_ID);
			const richTextBar = root.getElementById(RICH_TEXT_BAR_ID);
			const toolbarPlaceholder = document.getElementById('rich-text-toolbar-placeholder');
			const floatingToolbar = document.getElementById(RICH_TEXT_BAR_ID);

			// Don't update state if clicking on toolbar elements, dropdowns, overlays, or color pickers
			// This keeps the toolbar mounted during interactions
			if (
				fixedContainer?.contains(target) ||
				richTextBar?.contains(target) ||
				toolbarPlaceholder?.contains(target) ||
				floatingToolbar?.contains(target) ||
				target.closest('.bay-email-extensions-emailToolItem') ||
				target.closest('.arco-dropdown') ||
				target.closest('.arco-trigger-popup') ||
				target.closest('.arco-trigger') ||
				target.closest('.bay-email-color-picker-popup') ||
				target.closest('.bay-email-native-color-picker-popup') ||
				target.closest('[id="' + RICH_TEXT_BAR_ID + '"]')
			) {
				// Don't update contentEditableName - keep toolbar visible
				return;
			}

			const targetEditableElement = target?.closest('[contenteditable="true"]') || null;
			if (applyEditableState(targetEditableElement)) {
				return;
			}

			if (applyEditableState(getActiveEditableElement())) {
				return;
			}

			setTimeout(() => {
				if (!applyEditableState(getActiveEditableElement())) {
					setActive(null);
				}
			}, 0);
		};

		root.addEventListener('click', onClick);
		return () => {
			root.removeEventListener('click', onClick);
		};
	}, [setActive]);

	if (!contentEditableName) return null;

	return (
		<>
			<Field
				name={contentEditableName}
				parse={v => v}
			>
				{({ input }) => (
					<FieldWrapper
						{...props}
						contentEditableType={contentEditableType}
						tiptapEditor={active?.editor ?? null}
						input={input}
					/>
				)}
			</Field>
		</>
	);
};

function FieldWrapper(
	props: Omit<InlineTextProps, 'onChange'> & {
		input: FieldInputProps<any, HTMLElement>;
		contentEditableType: string | null;
		tiptapEditor: Editor | null;
	},
) {
	const { input, contentEditableType, tiptapEditor, ...rest } = props;
	const { mergeTagGenerate, enabledMergeTagsBadge } = useEditorProps();

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debounceCallbackChange = useCallback(
		debounce(val => {
			if (enabledMergeTagsBadge) {
				input.onChange(MergeTagBadge.revert(val, mergeTagGenerate));
			} else {
				input.onChange(val);
			}

			input.onBlur();
		}, 200),
		[input],
	);

	return (
		<>
			{contentEditableType === ContentEditableType.RichText &&
				tiptapEditor &&
				!tiptapEditor.isDestroyed && (
					<FloatingToolbar
						editor={tiptapEditor}
						anchor={tiptapEditor.view.dom as HTMLElement}
					/>
				)}
			<InlineText
				{...rest}
				onChange={debounceCallbackChange}
			/>
		</>
	);
}
